/** The Issue tab: the approval route, closing the period, and the filings issued (journey 4).
 *
 * The order is the order the work runs in: have it approved, reconcile and close, then issue. The
 * two panels with logic in them (`ClosePeriod`, `Filings`) are the original ones, moved unchanged.
 *
 * **Nothing here is disabled by the verdict.** Issuing is an Approver's act and is not gated on
 * the checks (ADR-0020, F07): whether a report may lawfully be filed is a person's judgement, and
 * the verdict travels inside the artifact so what was outstanding stays on the record.
 */

import type { Obligation } from "../api/queries";
import { Panel } from "../components/Panel";
import { ClosePeriod, Filings } from "./ReportsIssue";
import { RecordLink } from "./RecordLink";
import { ReportReviewPanel } from "./ReportsReview";
import own from "./Reports.module.css";
import type { Screen } from "./useScreen";

export function ReportsIssueTab({
  screen,
  active,
  role,
}: {
  screen: Screen;
  active: Obligation;
  role: string | undefined;
}) {
  return (
    <>
      <ReportReviewPanel obligationId={active.obligation_id} method={screen.method} />

      <Panel
        title="Approval route"
        sub="Preparing a filing and accepting it are separate acts"
        column="1 / 13"
      >
        {/* **The way to the filing's own record page — F-007.** A filing obligation is a record
            (`FILING_OBLIGATION`), and its approvals live on that page: this link is the clickable
            path to submit the filing for approval, which did not exist anywhere in the product.
            It carries the obligation's id — the same id `?filing=` names — never a regime and a
            date range. */}
        <p className={own.fine}>
          <RecordLink
            recordId={active.obligation_id}
            kind="filing_obligation"
            subject={`${active.regime_key.replace(/_/g, " ")}, ${active.legal_entity_name}`}
          />
        </p>
        <p className={own.fine}>
          Approvals are ordered stages with a named reviewer at each, and the person who submits a
          filing cannot decide their own request. They live on the filing&rsquo;s record page, with
          its notes and flags.
        </p>
      </Panel>

      {/* The period behind the selected filing: matched on **regime and dates**, because a window
          can belong to two regimes with different year ends and closing is regime-specific. */}
      <ClosePeriod
        periodId={
          screen.organization?.periods.find(
            (p) =>
              p.regime_key === active.regime_key &&
              p.start_date === screen.window?.period_start &&
              p.end_date === screen.window?.period_end,
          )?.id
        }
        canClose={role === "admin" || role === "approver"}
      />

      <Filings
        obligationId={active.obligation_id}
        method={screen.method}
        entityName={active.legal_entity_name}
      />
    </>
  );
}
